import { motion } from "framer-motion";
import { Phone, Building2, Repeat, FileCheck, ArrowRight } from "lucide-react";
import { BRAND } from "@/data/content";

const PERKS = [
  { icon: Repeat, label: "Reliable recurring schedules" },
  { icon: FileCheck, label: "Licensed, insured & W-9 ready" },
  { icon: Building2, label: "Multi-property volume pricing" },
];

export default function PropertyManagement() {
  return (
    <section
      className="relative py-20 md:py-24"
      data-testid="pm-cta-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2rem] bg-[#1c1917] text-white px-7 py-12 md:px-14 md:py-16 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.5)]"
        >
          {/* Decorative accents */}
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#16a34a]/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#eab308]/20 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-[#16a34a]/20 border border-[#16a34a]/30 text-[11px] uppercase tracking-[0.2em] font-bold text-[#86efac]">
                <Building2 className="w-3.5 h-3.5" />
                Property Management Companies
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tighter leading-[1.05]">
                Managing multiple properties?
                <br />
                <span className="text-[#eab308]">Let&rsquo;s get you a free quote.</span>
              </h2>
              <p className="mt-5 text-white/75 text-base md:text-lg max-w-xl leading-relaxed">
                We work directly with property managers, HOAs, and rental
                portfolios across Tampa Bay. One trusted crew, one invoice, and
                pricing that scales with you.
              </p>

              <ul className="mt-7 grid sm:grid-cols-3 gap-3">
                {PERKS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4"
                  >
                    <span className="h-9 w-9 rounded-xl bg-[#16a34a] inline-flex items-center justify-center flex-none">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-semibold leading-snug">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={BRAND.phoneHref}
                  data-testid="pm-phone-cta"
                  className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold transition active:scale-[0.98] shadow-[0_12px_30px_-10px_rgba(22,163,74,0.7)]"
                >
                  <Phone className="w-5 h-5 group-hover:rotate-12 transition" />
                  Call Now {BRAND.phone}
                </a>
                <a
                  href="#contact"
                  data-testid="pm-contact-cta"
                  className="inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold transition"
                >
                  Request a free quote
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <p className="mt-4 text-xs text-white/55">
                Lic. #L23000070666 · Insured · W-9 available on request
              </p>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-[0_25px_50px_-15px_rgba(0,0,0,0.4)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_craft-solutions-new/artifacts/aayi0l6a_image.png"
                  alt="ProCraft Solutions multi-property work — Tampa Bay"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#86efac]">
                    Now Serving
                  </div>
                  <div className="font-display font-extrabold text-xl mt-1">
                    Residential · Commercial · Rental Portfolios
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
